import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseLeaderboardAnonymityReturn {
  anonymous: boolean;
  loading: boolean;
  saving: boolean;
  error: string | null;
  setAnonymous: (next: boolean) => void;
}

/**
 * Read + toggle the caller's "hide me on the leaderboards" preference. On a
 * successful toggle, every leaderboard family is invalidated so displayed names
 * refresh immediately (a now-hidden user turns into "(hidden)" everywhere).
 * Deliberately does NOT invalidate `gameRewards.all` — that would stampede
 * subscriptions/store/rejections queries too.
 */
export function useLeaderboardAnonymity(): UseLeaderboardAnonymityReturn {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.gameRewards.leaderboardAnonymity(),
    queryFn: () => api.getGameRewardsLeaderboardAnonymity(),
  });

  const mutation = useMutation({
    mutationFn: (next: boolean) => api.setGameRewardsLeaderboardAnonymity(next),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.gameRewards.leaderboardAnonymity(), result);
      queryClient.invalidateQueries({ queryKey: [...queryKeys.gameRewards.all, 'leaderboard'] });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.gameRewards.all, 'timeLeaderboard'],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.gameRewards.all, 'rewardLeaderboard'],
      });
    },
  });

  return {
    anonymous: data?.anonymous ?? false,
    loading: isLoading,
    saving: mutation.isPending,
    error:
      error instanceof Error ? error.message : isError ? 'Failed to load anonymity setting' : null,
    setAnonymous: (next: boolean) => mutation.mutate(next),
  };
}
